import { SearchParamController } from '@clockout-dev/design-elements';
import ClockoutController from '../../../core/clockout.controller';
import Inherit from '../../../core/inherit';
import PageDelegate from './PageDelegate';

const DEFAULT_TABS_SEARCH_PARAM_KEY = 'tab';

/**
 * Stateless helpers for {@link Page}. Holds the small tab utilities so the
 * component file stays focused on the render. Mixed into the component via
 * `Inherit(PageController, React.Component)`, so `this` inside these
 * methods is the React component instance (state, props, setState all
 * available).
 *
 * URL search-param reads/writes are delegated to the
 * `SearchParamController` shipped by `@clockout-dev/design-elements`, so
 * the entire search-param contract for the page lives in one place.
 *
 * @extends {ClockoutController}
 * @extends {PageDelegate}
 * @extends {SearchParamController}
 */
class PageController extends Inherit(ClockoutController, PageDelegate, SearchParamController) {
    state = {
        activeTabKey: null
    };

    constructor(props) {
        super(props);
        // Seed internal state only in uncontrolled mode. When `activeTabKey`
        // is supplied as a prop the caller owns the value; internal state is
        // ignored in `resolveActiveTabKey()` below.
        if (props.tabs && props.activeTabKey === undefined) {
            this.state.activeTabKey = this.resolveInitialTabKey(props.tabs, props.tabsSearchParamKey);
        }
    }

    /**
     * Returns the URL search-param key the page uses to persist the
     * active tab. Prefers the `tabsSearchParamKey` prop, falling back to
     * `'tab'`.
     *
     * @returns {string}
     */
    getTabsSearchParamKey() {
        return this.props.tabsSearchParamKey || DEFAULT_TABS_SEARCH_PARAM_KEY;
    }

    /**
     * Returns the active tab key. When `activeTabKey` is provided as a
     * controlled prop, that value wins over internal state so programmatic
     * navigation (goToNextStep, navigateToStep) is always reflected in the
     * tab strip without a separate `setState` call.
     *
     * @returns {string|null}
     */
    resolveActiveTabKey() {
        if (this.props.activeTabKey !== undefined) return this.props.activeTabKey;
        return this.state.activeTabKey;
    }

    /**
     * Reads the active tab key from the URL via the shared
     * {@link SearchParamController}, falling back to the first tab when
     * the URL value does not match any of the provided tabs.
     *
     * @returns {string|null}
     */
    resolveInitialTabKey(tabs, paramKey) {
        if (!Array.isArray(tabs) || tabs.length === 0) return null;
        const key = paramKey || DEFAULT_TABS_SEARCH_PARAM_KEY;
        const fromUrl = this.getSearchParamValue(key);
        return tabs.some((t) => t.key === fromUrl) ? fromUrl : tabs[0].key;
    }

    /**
     * `onChange` handler forwarded to the design-elements `Tabs`. Writes
     * the new active key to the URL via the shared
     * {@link SearchParamController}, mirrors it into component state so
     * uncontrolled pages stay in sync, and forwards the key to the parent
     * via the optional `onTabChange` prop.
     *
     * @param {string} key new active tab key
     */
    handleTabChange(key) {
        this.setSearchParamValue(this.getTabsSearchParamKey(), key);
        // Only update internal state in uncontrolled mode — controlled
        // callers manage the active key themselves.
        if (this.props.activeTabKey === undefined) {
            this.setActiveTabKey(key);
        }
        if (typeof this.props.onTabChange === 'function') {
            this.props.onTabChange(key);
        }
    }
}

export default PageController;
